export const HOME_ARMS = ["ink-today", "ink-oddsjam", "classic-today", "classic-oddsjam"] as const;
export type HomeArm = (typeof HOME_ARMS)[number];
export type HomeColors = "ink" | "classic";
export type HomeLayout = "today" | "oddsjam";

export const HOME_CONTROL_ARM: HomeArm = "ink-today";
export const HOME_ARM_COOKIE = "upside_home_arm";
export const HOME_ARM_PARAM = "home_arm";
export const HOME_ARM_PATH = "/home-arm";
export const HOME_ARM_FLAG = "homepage_colors_layout_v1";
export const HOME_ARM_EXPOSURE = "homepage_arm_entered";
export const HOME_ARM_COOKIE_MAX_AGE = 60 * 60 * 24 * 90;

const CRAWLER = /bot\b|bot\/|crawler|spider|slurp|facebookexternalhit|bingpreview|embedly|whatsapp|lighthouse/i;

export function isHomeArm(value: unknown): value is HomeArm {
  return typeof value === "string" && (HOME_ARMS as readonly string[]).includes(value);
}

export function homeArmParts(arm: HomeArm): { colors: HomeColors; layout: HomeLayout } {
  const [colors, layout] = arm.split("-") as [HomeColors, HomeLayout];
  return { colors, layout };
}

export function isHomePath(pathname: string | null | undefined): boolean {
  return pathname === "/" || Boolean(pathname?.startsWith(`${HOME_ARM_PATH}/`));
}

export function chooseHomeArm({ cookie, param, userAgent, signedIn, random }: {
  cookie: string | undefined;
  param: string | null;
  userAgent: string;
  signedIn: boolean;
  random: number;
}): { arm: HomeArm; remember: boolean } | null {
  if (isHomeArm(param)) return { arm: param, remember: param !== cookie };
  if (signedIn || CRAWLER.test(userAgent)) return null;
  const arm: HomeArm = isHomeArm(cookie)
    ? `${homeArmParts(cookie).colors}-oddsjam`
    : random < 0.5 ? "ink-oddsjam" : "classic-oddsjam";
  return { arm, remember: arm !== cookie };
}

export const HOME_CARDS = ["none", "fantasy"] as const;
export type HomeCard = (typeof HOME_CARDS)[number];

export const HOME_CARD_COOKIE = "upside_home_card";
export const HOME_CARD_PARAM = "home_card";
export const HOME_CARD_FLAG = "homepage_fantasy_card_v1";
const CARD_SUFFIX = "-fantasy";

export function isHomeCard(value: unknown): value is HomeCard {
  return typeof value === "string" && (HOME_CARDS as readonly string[]).includes(value);
}

export function chooseHomeCard({ cookie, param, random }: {
  cookie: string | undefined;
  param: string | null;
  random: number;
}): { card: HomeCard; remember: boolean } {
  if (isHomeCard(param)) return { card: param, remember: param !== cookie };
  if (isHomeCard(cookie)) return { card: cookie, remember: false };
  return { card: random < 0.5 ? "none" : "fantasy", remember: true };
}

export function homePageSlug(arm: HomeArm, card: HomeCard): string | null {
  if (arm === HOME_CONTROL_ARM && card === "none") return null;
  return card === "fantasy" ? `${arm}${CARD_SUFFIX}` : arm;
}

export function homePageSlugs(): string[] {
  return HOME_ARMS.flatMap((arm) => HOME_CARDS.map((card) => homePageSlug(arm, card))).filter((slug): slug is string => slug !== null);
}

export function parseHomePageSlug(slug: string): { arm: HomeArm; card: HomeCard } | null {
  const card: HomeCard = slug.endsWith(CARD_SUFFIX) ? "fantasy" : "none";
  const arm = card === "fantasy" ? slug.slice(0, -CARD_SUFFIX.length) : slug;
  if (!isHomeArm(arm) || homePageSlug(arm, card) === null) return null;
  return { arm, card };
}
